import { safeUrl } from './contracts';
import { compileEmailBlocks } from './data/email-blocks';

const MISSING = /^\s*unable to (find the content data|find|read)\s*$/i;

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[character]));
}

function looksLikeHtml(value) {
  return typeof value === 'string' && /<[a-z][\s\S]*>/i.test(value) && !MISSING.test(value.trim());
}

function parseJson(value) {
  if (value && typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed[0] !== '{' && trimmed[0] !== '[') return null;
  try { return JSON.parse(trimmed); } catch (error) { return null; }
}

function isLegacyDesign(value) {
  if (!value || typeof value !== 'object') return false;
  if (Array.isArray(value))
    return value.some(item => item && (item.type || item.markup || item.modules || item.columns || item.blocks));
  return !!(value.page || value.rows || (Array.isArray(value.blocks) && value.blocks.length) ||
    value.columns || value.modules || value.markup || value.descriptor || looksLikeHtml(value.html));
}

function unwrapPayload(payload) {
  if (!payload || typeof payload !== 'object') return {};
  const nested = payload.data;
  if (nested && typeof nested === 'object' &&
    (nested.message != null || nested.emailContentSections != null || nested.json != null || nested.bodyHtml != null))
    return nested;
  return payload;
}

function cssFrom(style) {
  if (!style || typeof style !== 'object' || Array.isArray(style)) return '';
  return Object.keys(style).map(key => {
    const value = String(style[key] == null ? '' : style[key]).trim();
    if (!value || /expression|javascript|behavior|@import|binding/i.test(value)) return '';
    const property = key.replace(/[A-Z]/g, letter => '-' + letter.toLowerCase()).replace(/^-+/, '');
    if (!/^[a-z-]+$/.test(property)) return '';
    if (/url\s*\(/i.test(value) && !/url\s*\(\s*['"]?https?:\/\//i.test(value)) return '';
    return property + ':' + value.replace(/[;\r\n]/g, ' ');
  }).filter(Boolean).join(';');
}

function moduleKind(type) {
  return String(type || '').split('-').pop().toLowerCase();
}

function descriptorBody(module) {
  const desc = module.descriptor || module;
  const kind = moduleKind(module.type);
  return desc[kind] || desc.paragraph || desc.text || desc.heading || desc.image ||
    desc.button || desc.html || desc.list || desc.video || desc.gif || desc.icons ||
    desc.social || desc.menu || desc.divider || desc.spacer || desc;
}

function renderImage(src, alt, href, extraStyle) {
  if (!src || !safeUrl(src)) return '';
  const image = '<img src="' + escapeHtml(src) + '" alt="' + escapeHtml(alt || '') +
    '" style="max-width:100%;height:auto;border:0;display:block;' + (extraStyle || '') + '">';
  if (href && safeUrl(href)) return '<a href="' + escapeHtml(href) + '">' + image + '</a>';
  return image;
}

function renderButton(label, href, style) {
  const text = escapeHtml(label || 'Learn more');
  const link = href && safeUrl(href) ? href : '#';
  const buttonStyle = cssFrom(style) || 'background-color:#166534;color:#ffffff;padding:12px 24px;border-radius:4px;display:inline-block;text-decoration:none;font-weight:bold';
  return '<a href="' + escapeHtml(link) + '" style="' + buttonStyle + '">' + text + '</a>';
}

function renderIcons(list) {
  const icons = Array.isArray(list) ? list : (list && list.icons) || [];
  return icons.map(icon => {
    const image = icon.image || icon;
    const href = icon.href || image.href || '';
    const src = image.src || icon.src || '';
    const alt = image.alt || icon.alt || icon.name || icon.title || '';
    if (src) return renderImage(src, alt, href, 'display:inline-block;margin:0 4px;');
    if (href && safeUrl(href)) return '<a href="' + escapeHtml(href) + '">' + escapeHtml(alt || href) + '</a>';
    return '';
  }).filter(Boolean).join(' ');
}

function renderModule(module) {
  if (!module || typeof module !== 'object') return '';
  const kind = moduleKind(module.type);
  const desc = module.descriptor || module;
  const body = descriptorBody(module) || {};
  const pad = cssFrom(desc.style);
  const wrap = html => html ? '<table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td style="' + pad + '">' + html + '</td></tr></table>' : '';
  if (kind === 'heading') {
    const tag = /h[1-6]/i.test(body.title) ? String(body.title).toLowerCase() : 'h1';
    const text = looksLikeHtml(body.html) ? body.html : (looksLikeHtml(body.text) ? body.text : escapeHtml(body.text || body.html || ''));
    return wrap('<' + tag + ' style="' + cssFrom(body.style) + '">' + text + '</' + tag + '>');
  }
  if (kind === 'paragraph' || kind === 'text' || kind === 'list') {
    const html = looksLikeHtml(body.html) ? body.html : (looksLikeHtml(body.text) ? body.text : (body.html || body.text ? '<p>' + escapeHtml(body.html || body.text) + '</p>' : ''));
    return wrap(html);
  }
  if (kind === 'html') return wrap(body.html || body.text || desc.html || '');
  if (kind === 'image' || kind === 'gif')
    return wrap(renderImage(body.src, body.alt, body.href, cssFrom(body.style)));
  if (kind === 'button') return wrap(renderButton(body.label || body.text, body.href, body.style));
  if (kind === 'divider')
    return wrap('<hr style="border:0;border-top:1px solid #e2e8f0;margin:8px 0;">');
  if (kind === 'spacer') {
    const height = Math.max(4, Math.min(120, parseInt(body.height || desc.height || 24, 10) || 24));
    return wrap('<div style="height:' + height + 'px;line-height:' + height + 'px;font-size:1px;">&nbsp;</div>');
  }
  if (kind === 'video') {
    const href = body.src || body.url || body.href || (body.thumb && body.thumb.href);
    const src = (body.thumb && body.thumb.src) || body.thumbnailSrc || body.thumbnailUrl || '';
    if (src) return wrap(renderImage(src, body.alt || 'Watch the video', href));
    if (href && safeUrl(href)) return wrap('<a href="' + escapeHtml(href) + '">Watch the video</a>');
    return '';
  }
  if (kind === 'social' || kind === 'icons' || kind === 'menu')
    return wrap(renderIcons(body.icons || body.iconsList || desc.icons || desc.iconsList || body));
  const html = looksLikeHtml(body.html) ? body.html : (looksLikeHtml(module.html) ? module.html : '');
  if (html) return wrap(html);
  if (body.src) return wrap(renderImage(body.src, body.alt, body.href));
  if (body.label || body.text) return wrap(renderButton(body.label || body.text, body.href, body.style));
  return '';
}

function renderColumns(columns, rowStyle) {
  const cells = (columns || []).map(column => {
    const width = column['grid-columns'] || column.gridColumns || column.width;
    const percent = width ? (Number(width) / 12 * 100) + '%' : '';
    const modules = (column.modules || []).map(renderModule).join('');
    return '<td valign="top" width="' + escapeHtml(percent) + '" style="width:' + escapeHtml(percent) + ';' +
      cssFrom(column.style) + '">' + modules + '</td>';
  }).join('');
  return '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="' + cssFrom(rowStyle) +
    '"><tr>' + cells + '</tr></table>';
}

function beefreeToHtml(design) {
  const page = design.page || design;
  const rows = page.rows || design.rows || [];
  const body = page.body || {};
  const computed = (body.content && body.content.computedStyle) || {};
  const container = (body.container && body.container.style) || {};
  const width = computed.messageWidth || (body.content && body.content.style && body.content.style.width) || '600px';
  const pageBackground = computed.backgroundColor || container['background-color'] || '#f3f4f6';
  const messageBackground = computed.messageBackgroundColor || '#ffffff';
  const inner = rows.map(row => {
    const contentStyle = (row.content && row.content.style) || row.style || {};
    return renderColumns(row.columns, contentStyle);
  }).join('');
  if (!inner) return '';
  return '<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">' +
    '<style>body{margin:0;padding:0;background:' + escapeHtml(pageBackground) + ';}img{max-width:100%;height:auto;}</style></head><body>' +
    '<table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:16px 8px;">' +
    '<table role="presentation" width="' + escapeHtml(String(parseInt(width, 10) || 600)) +
    '" cellpadding="0" cellspacing="0" style="width:100%;max-width:' + escapeHtml(width) +
    ';background:' + escapeHtml(messageBackground) + ';">' +
    '<tr><td>' + inner + '</td></tr></table></td></tr></table></body></html>';
}

function grooveSectionToHtml(section) {
  if (!section || typeof section !== 'object') return '';
  const data = section.data || {};
  if (section.type === 'column' && Array.isArray(section.children)) {
    const width = (100 / Math.max(1, section.children.length)) + '%';
    const cells = section.children.map(child =>
      '<td valign="top" width="' + width + '">' + (looksLikeHtml(child.markup) ? child.markup : escapeHtml(child.markup || '')) + '</td>'
    ).join('');
    return '<table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>' + cells + '</tr></table>';
  }
  if (section.type === 'image' || data.url)
    return renderImage(data.url || data.imageUrl || data.src, data.alt, data.href || data.actionUrl || section.actionUrl);
  if (section.type === 'button')
    return renderButton(section.markup || data.label, data.url || data.href || section.actionUrl, data.style);
  if (section.type === 'divider') return '<hr style="border:0;border-top:1px solid #e2e8f0;margin:8px 0;">';
  if (section.type === 'video') {
    const href = data.url || data.href || section.actionUrl;
    return href && safeUrl(href) ? '<a href="' + escapeHtml(href) + '">Watch the video</a>' : '';
  }
  return looksLikeHtml(section.markup) ? section.markup : (section.markup ? '<p>' + escapeHtml(section.markup) + '</p>' : '');
}

function harvestHtml(value, found) {
  if (!value) return;
  if (typeof value === 'string') {
    if (looksLikeHtml(value) && value.length < 200000) found.push(value);
    return;
  }
  if (Array.isArray(value)) { value.forEach(item => harvestHtml(item, found)); return; }
  if (typeof value !== 'object') return;
  ['html', 'text', 'markup', 'label'].forEach(key => {
    if (typeof value[key] === 'string' && value[key] && looksLikeHtml(value[key])) found.push(value[key]);
    else if (typeof value[key] === 'string' && value[key] && key === 'label') found.push(escapeHtml(value[key]));
  });
  if (value.src) {
    const image = renderImage(value.src, value.alt, value.href);
    if (image) found.push(image);
  }
  Object.keys(value).forEach(key => {
    if (['html', 'text', 'markup', 'style', 'computedStyle', 'mobileStyle', 'src', 'alt', 'href', 'label'].includes(key)) return;
    harvestHtml(value[key], found);
  });
}

export function legacyDesignToHtml(design) {
  if (!design || typeof design !== 'object') return '';
  if (Array.isArray(design.blocks) && (design.version || design.blocks[0])) {
    try { return compileEmailBlocks(design); } catch (error) { /* fall through to other formats */ }
  }
  if (design.page || Array.isArray(design.rows)) {
    const html = beefreeToHtml(design);
    if (html) return html;
  }
  if (Array.isArray(design)) return design.map(grooveSectionToHtml).filter(Boolean).join('');
  if (design.type || design.markup) return grooveSectionToHtml(design);
  const found = [];
  harvestHtml(design, found);
  return found.join('');
}

function fieldToHtml(field) {
  if (field == null || field === '') return '';
  if (typeof field === 'string' && MISSING.test(field.trim())) return '';
  const design = parseJson(field);
  if (isLegacyDesign(design)) {
    const html = legacyDesignToHtml(design);
    if (html && /<[a-z][\s\S]*>/i.test(html)) return html;
    return '';
  }
  if (looksLikeHtml(field)) return field;
  return '';
}

// Read-only: pick HTML or convert a legacy design. Never mutates the payload.
export function savedTemplateHtml(payload) {
  const record = unwrapPayload(payload);
  const fields = [
    record.message, record.html, record.content, record.bodyHtml, record.body,
    record.emailcontent, record.emailContentSections, record.json, record.emailSections
  ];
  for (let index = 0; index < fields.length; index++) {
    const html = fieldToHtml(fields[index]);
    if (html) return html;
  }
  return '';
}
